import { AxiosInstance } from "axios";

// Array to track all axios instances that need auth tokens
const axiosInstances: AxiosInstance[] = [];

// Async function that returns a fresh access token (e.g. getAccessTokenSilently)
let tokenProvider: (() => Promise<string>) | null = null;

// In-flight promise cache to coalesce concurrent token refreshes.
// When multiple Axios requests fire simultaneously (e.g. TanStack Query
// parallel queries on mount), they share a single token refresh call.
let inflightTokenPromise: Promise<string> | null = null;

// Register an axios instance to receive auth tokens.
// Adds a request interceptor that fetches a fresh token before each request
// when a token provider has been registered via setTokenProvider.
export const registerAxiosInstance = (instance: AxiosInstance): void => {
  if (!axiosInstances.includes(instance)) {
    axiosInstances.push(instance);

    instance.interceptors.request.use(async (config) => {
      if (tokenProvider) {
        try {
          if (!inflightTokenPromise) {
            inflightTokenPromise = tokenProvider().finally(() => {
              inflightTokenPromise = null;
            });
          }
          const token = await inflightTokenPromise;
          config.headers.Authorization = `Bearer ${token}`;
        } catch (error) {
          // Token refresh failed; log for debugging and let the request
          // proceed without an updated header so the 401 interceptor
          // can handle it.
          console.warn(
            "[AxiosTokenManager] Failed to refresh access token before request.",
            error,
          );
        }
      }
      return config;
    });
  }
};

// Register a function that provides fresh access tokens per-request.
// Auth0's getAccessTokenSilently returns from cache when valid and
// transparently refreshes when the token is near expiry.
export const setTokenProvider = (
  provider: () => Promise<string>,
): void => {
  tokenProvider = provider;
};

// Remove the registered token provider (e.g. on logout) so stale
// closures over getAccessTokenSilently are not retained.
export const clearTokenProvider = (): void => {
  tokenProvider = null;
};

// Set a static access token on all registered axios instances.
// Used for the initial token injection and by MockAuthProvider.
export const setAccessToken = (token: string): void => {
  const authHeader = `Bearer ${token}`;
  axiosInstances.forEach((instance) => {
    instance.defaults.headers.common["Authorization"] = authHeader;
  });
};
